import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button, Input, Alert } from '../components/ui';
import { authApi } from '../services/api';
import { setToken, setRefreshToken } from '../services/tokenStorage';

export default function SetPasswordPage() {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const rt = searchParams.get('rt');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!password || password.length < 6) {
      setError(t('setPassword.minLength'));
      return;
    }

    if (!token) {
      setError(t('setPassword.tokenNotFound'));
      return;
    }

    setIsLoading(true);
    try {
      // Yangi api route-ga POST qilamiz
      const res = await authApi.setupPassword(password.trim(), token);
      
      if (res?.status === 'SUCCESS' || res?.message === 'PASSWORD_SET_SUCCESSFUL') {
        // Access + Refresh tokenlarni to'g'ri saqlash
        setToken(token);
        if (rt) setRefreshToken(decodeURIComponent(rt));
        window.location.href = '/main';
      } else {
        setError(res?.message || res?.reason || t('setPassword.genericError'));
      }
    } catch (err) {
      setError(err.message || t('setPassword.genericError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 dark:from-[#080f0b] dark:via-[#0c1510] dark:to-[#080f0b] flex items-center justify-center px-4 py-8 overflow-hidden">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full bg-[#1e9f6b]/10 blur-[80px]" />
        <div className="absolute -bottom-32 -left-32 h-[400px] w-[400px] rounded-full bg-sky-400/8 blur-[80px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[300px] w-[300px] rounded-full bg-[#1e9f6b]/5 blur-[60px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md relative z-10"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="bg-white dark:bg-slate-900 rounded-[40px] border border-slate-100 dark:border-slate-800/50 shadow-2xl shadow-slate-900/10 p-10 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-[40px] pointer-events-none" />

          <div className="text-center mb-8">
            <h2 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-wider mb-2">{t('setPassword.title')}</h2>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              {t('setPassword.subtitle')}
            </p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-6">
            <Input
              label={t('setPassword.newPasswordLabel')}
              type={showPwd ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              icon="lock"
              rightIcon={showPwd ? 'visibility_off' : 'visibility'}
              onRightIconClick={() => setShowPwd(!showPwd)}
              required
              className="h-14 font-black tracking-widest italic shadow-sm"
            />

            <Alert message={error} type="error" />

            <Button type="submit" size="xl" loading={isLoading}
              className="h-16 font-black uppercase tracking-[0.2em] text-sm shadow-xl shadow-[#1e9f6b]/30 active:scale-[0.98]">
              {t('setPassword.submit')}
            </Button>
          </form>
        </motion.div>
      </motion.div>
    </div>
  );
}
